import { LocalDateTime } from '@js-joda/core';
import {
  Box,
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { Trans } from '@lingui/react';
import { i18n } from 'i18n';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AlertText from 'components/AlertText';
import { FormikActions, FormSpacing, ProjectNumberField } from 'components/Form';
import { Dayjs } from 'dayjs';
import { BasicDisplayUnits, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import useForm from 'hooks/useForm';
import { useProject } from 'modules/project/providers/project';
import {
  ApiException,
  BackfillRecipeDto,
  BackfillWarningDto,
  CustomUnitFields,
  FillType,
  PourMutation,
  UnitSystem,
  useMineModelRheologyDataListQuery
} from 'providers/api';
import { computePourDurationHours } from 'modules/project/ProjectAreaPage/ProjectReportsPage/pourReportUtils';
import { HOUR_IN_MILLISECONDS } from 'config';
import React, { useState } from 'react';
import { UseMutationResult } from 'react-query';
import { dayJsToLocalDateTime, LocalDateTimeToDayJs } from 'utils';
import RecipeField from 'modules/project/StopeFillPourForm/RecipeField';
import { generatePourMutationSchema, StopeFillPourFormInputs } from 'modules/project/StopeFillPourForm/PourSchema';
import WarningListInput from 'modules/project/NewPourPage/PourForm/WarningListInput';
import UnitSystemNumberField from 'modules/project/ProjectAreaPage/ProjectStopesPage/StopePanelPage/RecipesTab/RecipeForm/UnitSystemNumberField';
import { updateStopeFillSceneState, InputMode } from 'modules/project/ProjectAreaPage/ProjectStopesPage/Scenes/StopeFillScene/stopeFillSceneState';

interface StopeFillPourFormSceneProps {
  projectId: string;
  stopeId: string;
  unitSystemPreference: UnitSystem;
  fillType: FillType;
  mutation: UseMutationResult<string, unknown, PourMutation, unknown>;
  customUnitFields: CustomUnitFields[];
  onPreviewPour: (volume: number, height?: number, beachAngleDegrees?: number) => void;
  onRecipeChange: (entityId: string) => void;
  onCreateCompleted: () => void;
  pipePosition: { x: number; y: number } | null;
  pourHeight: number | null;
  setPourHeight: (height: number | null) => void;
  // Both take the beach angle so a change to the per-pour override re-derives against a fresh curve
  // rather than a stale one; undefined means "use the project default".
  computeVolumeFromHeight: (height: number, beachAngleDegrees?: number) => number;
  computeHeightFromVolume?: (volume: number, beachAngleDegrees?: number) => number;
  // Resolved project default, shown as the placeholder and used when the override is blank.
  projectBeachAngleDegrees: number;
}

const StopeFillPourFormScene = ({
  projectId,
  stopeId,
  unitSystemPreference,
  fillType,
  mutation,
  customUnitFields,
  onPreviewPour,
  onRecipeChange,
  onCreateCompleted,
  pipePosition,
  pourHeight,
  setPourHeight,
  computeVolumeFromHeight,
  computeHeightFromVolume,
  projectBeachAngleDegrees,
}: StopeFillPourFormSceneProps) => {
  const { state: { timeZone } } = useProject();
  const minDateRef = React.useRef(
    LocalDateTime
      .now(timeZone)
      .withHour(0)
      .withMinute(0)
      .withSecond(0)
      .withNano(0),
  );

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [inputMode, setInputMode] = useState<InputMode>('height');
  const [recipeWarnings, setRecipeWarnings] = useState<BackfillWarningDto[]>([]);
  // The whole recipe, not just its id: the duration estimate needs its throughput specification, and the
  // pour does not exist yet to read one off.
  const [selectedRecipe, setSelectedRecipe] = useState<BackfillRecipeDto | null>(null);

  const { data: rheologyList } = useMineModelRheologyDataListQuery(
    { mineModelId: projectId, rheologyDataSetIds: [] },
    { enabled: !!projectId, staleTime: HOUR_IN_MILLISECONDS },
  );

  const mutationErrorMessage = React.useMemo(() => {
    if (!mutation.isError || !mutation.error) return null;

    if (ApiException.isApiException(mutation.error)) {
      const apiError = mutation.error;
      return apiError.result?.detail
        || apiError.result?.title
        || apiError.message;
    }

    return (mutation.error as Error).message;
  }, [mutation.error, mutation.isError]);

  const isWarningError = React.useMemo(() => {
    if (!mutation.isError || !mutation.error) return false;

    if (ApiException.isApiException(mutation.error)) {
      return (mutation.error.result?.status ?? mutation.error.status) === 406;
    }

    return false;
  }, [mutation.error, mutation.isError]);

  const {
    formik,
    helpers,
  } = useForm<StopeFillPourFormInputs>({
    mutation,
    formikConfig: {
      initialValues: {
        stopeId,
        data: {
          acceptedWarnings: [],
          reference: '',
          plannedDate: null,
          pourPositionX: pipePosition?.x,
          pourPositionY: pipePosition?.y,
          pourHeight: undefined,
          volume: undefined,
          pasteBeachAngleDegrees: undefined,
        },
      },
      onSubmit: (pourForm, { setSubmitting }) => {
        setSubmitting(true);
        const pourPayload: PourMutation = {
          recipeId: pourForm.recipeId!,
          recipeReference: pourForm.recipeReference!,
          stopeId: pourForm.stopeId!,
          data: {
            plannedDate: pourForm.data.plannedDate!,
            reference: pourForm.data.reference!,
            acceptedWarnings: pourForm.data.acceptedWarnings ?? [],
            volume: pourForm.data.volume!,
            volumeRetainedAfterOutflow: pourForm.data.volumeRetainedAfterOutflow,
            pourPositionX: pourForm.data.pourPositionX,
            pourPositionY: pourForm.data.pourPositionY,
            pourHeight: pourForm.data.pourHeight,
            // Left undefined when the user did not override, so the backend resolves the project default.
            pasteBeachAngleDegrees: pourForm.data.pasteBeachAngleDegrees,
          },
        };
        mutation.mutate(pourPayload, {
          onSuccess: () => {
            onCreateCompleted?.();
          },
          onSettled: () => {
            setSubmitting(false);
          },
        });
      },
      validationSchema: generatePourMutationSchema(recipeWarnings.length, minDateRef.current),
    },
  });

  const {
    values,
    handleBlur,
    handleSubmit,
    setFieldValue,
    validateForm,
    setErrors,
  } = formik;

  // Estimated from the chosen recipe's throughput, by the same function the pour report and the volume
  // panel use once the pour exists - so the figure shown here is the figure it will be reported with.
  // Stays undefined until both a recipe and a volume are in hand; there is nothing sensible to show for
  // a recipe whose rheology carries no flow rate, and a default would read as a real estimate.
  const estimatedDurationHours = React.useMemo(() => {
    const volume = values.data?.volume;
    if (!selectedRecipe || !volume || volume <= 0) return undefined;

    const rheology = rheologyList?.find((r) => r.rheologyDataSetId === selectedRecipe.rheologyDataSetId);
    return computePourDurationHours(volume, selectedRecipe, rheology, fillType);
  }, [values.data?.volume, selectedRecipe, rheologyList, fillType]);

  const handlePlannedDateChange = async (value: Dayjs | null) => {
    if (value === null) setFieldValue('data.plannedDate', value);
    if (value) {
      const processedDateTime = dayJsToLocalDateTime(value);
      setFieldValue('data.plannedDate', processedDateTime);
      const e = await validateForm({
        ...values,
        data: {
          ...values.data,
          plannedDate: processedDateTime,
        },
      });
      setErrors(e);
    }
  };

  const handleRecipeChange = (recipe: BackfillRecipeDto | null) => {
    if (recipe) {
      setFieldValue('recipeReference', recipe.reference);
      setFieldValue('recipeId', recipe.entityId);
      setFieldValue('data.acceptedWarnings', []);
      setRecipeWarnings(recipe.warnings ?? []);
      setSelectedRecipe(recipe);
      onRecipeChange(recipe.entityId);
    }
  };

  const handleAcceptedWarningsChange = (warningCode: string) => {
    const current = values.data?.acceptedWarnings ?? [];
    const updated = current.some((code) => code === warningCode)
      ? current.filter((code) => code !== warningCode)
      : [...current, warningCode];
    setFieldValue('data.acceptedWarnings', updated);
  };

  const handleInputModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: InputMode | null) => {
    if (newMode !== null) {
      setInputMode(newMode);
      updateStopeFillSceneState({ inputMode: newMode });
    }
  };

  const beachAngleOverride = values.data?.pasteBeachAngleDegrees;

  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextHeight = Number(e.target.value);
    if (!Number.isFinite(nextHeight)) return;
    setPourHeight(nextHeight);
    setFieldValue('data.pourHeight', nextHeight);
    const computedVolume = computeVolumeFromHeight(nextHeight, beachAngleOverride);
    setFieldValue('data.volume', computedVolume);
  };

  // A beach angle change reshapes the fill surface, so it must re-derive the paired figure AND refresh
  // the 3D preview immediately - a preview left on the previous angle is simply wrong.
  const handleBeachAngleChange = (parsed: number | undefined) => {
    const nextAngle = parsed !== undefined && Number.isFinite(parsed) ? parsed : undefined;
    setFieldValue('data.pasteBeachAngleDegrees', nextAngle);

    if (inputMode === 'height') {
      const height = pourHeight ?? values.data?.pourHeight;
      if (height && height > 0) {
        const computedVolume = computeVolumeFromHeight(height, nextAngle);
        setFieldValue('data.volume', computedVolume);
        if (computedVolume > 0) onPreviewPour(computedVolume, height, nextAngle);
      }
      return;
    }

    const volume = values.data?.volume;
    if (volume && volume > 0 && computeHeightFromVolume) {
      const computedHeight = computeHeightFromVolume(volume, nextAngle);
      setPourHeight(computedHeight);
      setFieldValue('data.pourHeight', computedHeight);
      onPreviewPour(volume, computedHeight, nextAngle);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextVolume = Number(e.target.value);
    if (!Number.isFinite(nextVolume)) return;
    setFieldValue('data.volume', nextVolume);
    updateStopeFillSceneState({ currentVolume: nextVolume });
    if (computeHeightFromVolume) {
      const computedHeight = computeHeightFromVolume(nextVolume, beachAngleOverride);
      setPourHeight(computedHeight);
      setFieldValue('data.pourHeight', computedHeight);
    }
  };

  const triggerPreviewUpdate = React.useCallback(() => {
    const volume = values.data?.volume;
    const height = values.data?.pourHeight;
    if (volume && volume > 0) {
      onPreviewPour(volume, height, values.data?.pasteBeachAngleDegrees);
    }
  }, [values.data?.volume, values.data?.pourHeight, values.data?.pasteBeachAngleDegrees, onPreviewPour]);

  const handleHeightBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleBlur(e);
    triggerPreviewUpdate();
  };

  const handleVolumeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    handleBlur(e);
    triggerPreviewUpdate();
  };

  const handleHeightKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      triggerPreviewUpdate();
    }
  };

  const handleVolumeKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      triggerPreviewUpdate();
    }
  };

  const prevPipePositionRef = React.useRef<{ x: number; y: number } | null>(null);

  // Sits between the figure you type and the figure derived from it in BOTH modes, since it is what
  // converts one into the other. Held as a const rather than repeated in each branch.
  const beachAngleField = (
    <Box>
      <ProjectNumberField
        decimalPlaces={2}
        fullWidth
        id="pasteBeachAngleDegrees"
        name="pasteBeachAngleDegrees"
        label={<Trans id="Beach Angle (deg)">Beach Angle (deg)</Trans>}
        value={values.data?.pasteBeachAngleDegrees ?? null}
        placeholder={`${projectBeachAngleDegrees}`}
        onChange={(parsed) => handleBeachAngleChange(parsed)}
        onBlur={handleBlur}
        error={helpers.hasError('data.pasteBeachAngleDegrees')}
        helperText={helpers.getErrorHelpText('data.pasteBeachAngleDegrees')
          || i18n._('Leave blank to use the project default ({angle} deg)', { angle: projectBeachAngleDegrees })}
      />
    </Box>
  );

  React.useEffect(() => {
    if (!pipePosition) return;
    if (values.data?.pourPositionX !== pipePosition.x) {
      setFieldValue('data.pourPositionX', pipePosition.x);
    }
    if (values.data?.pourPositionY !== pipePosition.y) {
      setFieldValue('data.pourPositionY', pipePosition.y);
    }
  }, [pipePosition, setFieldValue, values.data?.pourPositionX, values.data?.pourPositionY]);

  React.useEffect(() => {
    const prevPos = prevPipePositionRef.current;
    const positionChanged = prevPos
      && pipePosition
      && (prevPos.x !== pipePosition.x || prevPos.y !== pipePosition.y);

    if (!positionChanged) {
      prevPipePositionRef.current = pipePosition;
      return;
    }

    if (inputMode === 'height') {
      if (pourHeight && pourHeight > 0) {
        const newVolume = computeVolumeFromHeight(pourHeight, beachAngleOverride);
        setFieldValue('data.volume', newVolume);
      }
    } else {
      const currentVolume = values.data?.volume;
      if (currentVolume && currentVolume > 0 && computeHeightFromVolume) {
        const newHeight = computeHeightFromVolume(currentVolume, beachAngleOverride);
        setPourHeight(newHeight);
        setFieldValue('data.pourHeight', newHeight);
      }
    }

    prevPipePositionRef.current = pipePosition;
  }, [pipePosition, pourHeight, inputMode, values.data?.volume, computeVolumeFromHeight, computeHeightFromVolume, setFieldValue, setPourHeight]);

  const prevComputeFnRef = React.useRef(computeVolumeFromHeight);

  React.useEffect(() => {
    if (inputMode !== 'height') return;

    const fnChanged = prevComputeFnRef.current !== computeVolumeFromHeight;
    prevComputeFnRef.current = computeVolumeFromHeight;

    if (fnChanged && pourHeight && pourHeight > 0) {
      const newVolume = computeVolumeFromHeight(pourHeight, beachAngleOverride);
      if (newVolume > 0) {
        setFieldValue('data.volume', newVolume);
      }
    }
  }, [computeVolumeFromHeight, pourHeight, setFieldValue, inputMode]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" gutterBottom>
          <Trans id="New Pour">New Pour</Trans>
        </Typography>
      </Box>

      {mutationErrorMessage && (
        <Box sx={{ mb: 2 }}>
          <AlertText severity={isWarningError ? 'warning' : 'error'}>
            {mutationErrorMessage}
          </AlertText>
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Box
          component={Paper}
          sx={{
            p: 2,
            mb: 1,
          }}
        >
          <FormSpacing>
            <Box>
              <RecipeField
                autoFocus
                required
                onChange={handleRecipeChange}
                stopeId={stopeId}
                projectId={projectId}
              />
            </Box>

            {recipeWarnings.length > 0 && (
              <WarningListInput
                acceptedWarnings={values.data?.acceptedWarnings ?? []}
                warnings={recipeWarnings}
                disabled={false}
                onChange={handleAcceptedWarningsChange}
              />
            )}

            <Box>
              <TextField
                fullWidth
                id="reference"
                name="reference"
                label={<Trans id="Pour Reference">Pour Reference</Trans>}
                value={values.data?.reference}
                onChange={(e) => setFieldValue('data.reference', e.target.value)}
                onBlur={handleBlur}
                error={helpers.hasError('data.reference')}
                helperText={helpers.getErrorHelpText('data.reference')}
              />
            </Box>

            <Box>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <DatePicker
                  open={datePickerOpen}
                  onOpen={() => setDatePickerOpen(true)}
                  onClose={() => setDatePickerOpen(false)}
                  format="DD/MM/YYYY"
                  name="plannedDate"
                  label={i18n._('Pour Date').concat(` (${timeZone})`)}
                  value={
                    values.data?.plannedDate
                      ? LocalDateTimeToDayJs(values.data?.plannedDate)
                      : null
                  }
                  onChange={handlePlannedDateChange}
                  slotProps={{
                    textField: {
                      helperText: helpers.getErrorHelpText('data.plannedDate'),
                      error: helpers.hasError('data.plannedDate'),
                      autoComplete: 'off',
                      placeholder: '__/__/____',
                      onClick: () => {
                        setDatePickerOpen(true);
                        setFieldValue('data.plannedDate', null);
                      },
                      onBlur: handleBlur,
                    },
                  }}
                  sx={{
                    width: '100%',
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                <Trans id="Input Mode">Input Mode</Trans>
              </Typography>
              <ToggleButtonGroup
                value={inputMode}
                exclusive
                onChange={handleInputModeChange}
                aria-label="input mode"
                fullWidth
                size="small"
              >
                <ToggleButton value="height" aria-label="height input">
                  <Trans id="Height">Height</Trans>
                </ToggleButton>
                <ToggleButton value="volume" aria-label="volume input">
                  <Trans id="Volume">Volume</Trans>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {inputMode === 'height' && (
              <>
                <Box>
                  <UnitSystemNumberField
                    unitSystem={unitSystemPreference}
                    customUnitFields={customUnitFields}
                    unitInfo={unitLabels[BasicDisplayUnits.Distance]}
                    decimalPlaces={2}
                    type="number"
                    required
                    fullWidth
                    id="pourHeight"
                    name="pourHeight"
                    label={<Trans id="Pour Height">Pour Height</Trans>}
                    value={pourHeight ?? values.data?.pourHeight}
                    onChange={handleHeightChange}
                    onBlur={handleHeightBlur}
                    onKeyUp={handleHeightKeyUp}
                    error={helpers.hasError('data.pourHeight')}
                    helperText={helpers.getErrorHelpText('data.pourHeight')}
                  />
                </Box>
                {beachAngleField}
                <Box>
                  <UnitSystemNumberField
                    unitSystem={unitSystemPreference}
                    customUnitFields={customUnitFields}
                    unitInfo={unitLabels[BasicDisplayUnits.Volume]}
                    decimalPlaces={2}
                    type="number"
                    fullWidth
                    id="volume"
                    name="volume"
                    label={<Trans id="Calculated Volume">Calculated Volume</Trans>}
                    value={values.data?.volume}
                    disabled
                  />
                </Box>
              </>
            )}

            {inputMode === 'volume' && (
              <>
                <Box>
                  <UnitSystemNumberField
                    unitSystem={unitSystemPreference}
                    customUnitFields={customUnitFields}
                    unitInfo={unitLabels[BasicDisplayUnits.Volume]}
                    decimalPlaces={2}
                    type="number"
                    required
                    fullWidth
                    id="volume"
                    name="volume"
                    label={<Trans id="Volume">Volume</Trans>}
                    value={values.data?.volume}
                    onChange={handleVolumeChange}
                    onBlur={handleVolumeBlur}
                    onKeyUp={handleVolumeKeyUp}
                    error={helpers.hasError('data.volume')}
                    helperText={helpers.getErrorHelpText('data.volume')}
                  />
                </Box>
                {beachAngleField}
                <Box>
                  <UnitSystemNumberField
                    unitSystem={unitSystemPreference}
                    customUnitFields={customUnitFields}
                    unitInfo={unitLabels[BasicDisplayUnits.Distance]}
                    decimalPlaces={2}
                    type="number"
                    fullWidth
                    id="pourHeight"
                    name="pourHeight"
                    label={<Trans id="Calculated Height">Calculated Height</Trans>}
                    value={pourHeight ?? values.data?.pourHeight}
                    disabled
                  />
                </Box>
              </>
            )}

            {/* Outside both mode blocks so it reads the same either way: whatever the volume came from,
                this is how long it takes to place. Not a UnitSystemNumberField - hours are hours. */}
            <Box>
              <TextField
                fullWidth
                disabled
                id="estimatedDuration"
                name="estimatedDuration"
                label={<Trans id="Estimated Duration (h)">Estimated Duration (h)</Trans>}
                value={estimatedDurationHours !== undefined ? estimatedDurationHours.toFixed(1) : ''}
                // Only once there is a volume to convert: before that the field is empty because the
                // form is half-filled, not because anything is wrong with the recipe.
                helperText={estimatedDurationHours === undefined && !!values.data?.volume
                  ? i18n._('Select a recipe with a throughput to estimate the duration')
                  : undefined}
              />
            </Box>

            {fillType === FillType.Hydraulic && (
              <>
                <Box>
                  <ProjectNumberField
                    required
                    fullWidth
                    id="volumeRetainedAfterOutflow"
                    name="volumeRetainedAfterOutflow"
                    decimalPlaces={2}
                    label={<Trans id="Volume Retained After Outflow (%)">Volume Retained After Outflow (%)</Trans>}
                    value={values.data?.volumeRetainedAfterOutflow}
                    onChange={(parsed) => setFieldValue('data.volumeRetainedAfterOutflow', parsed)}
                    onBlur={handleBlur}
                    error={helpers.hasError('data.volumeRetainedAfterOutflow')}
                    helperText={helpers.getErrorHelpText('data.volumeRetainedAfterOutflow')}
                    slotProps={{
                      input: {
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      },
                    }}
                  />
                </Box>

                <Box>
                  <UnitSystemNumberField
                    unitSystem={unitSystemPreference}
                    customUnitFields={customUnitFields}
                    unitInfo={unitLabels[BasicDisplayUnits.Volume]}
                    decimalPlaces={2}
                    type="number"
                    fullWidth
                    label={<Trans id="Final Fill Volume">Final Fill Volume</Trans>}
                    value={
                      values.data?.volumeRetainedAfterOutflow && values.data?.volume
                        ? values.data?.volume * (values.data.volumeRetainedAfterOutflow / 100)
                        : values.data?.volume
                    }
                    InputLabelProps={{
                      shrink: Boolean(values.data?.volumeRetainedAfterOutflow || values.data?.volume),
                    }}
                    onChange={(e) => setFieldValue('data.finalFillVolume', e.target.value)}
                  />
                </Box>
              </>
            )}

            {pipePosition && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <Trans id="Pour Position">Pour Position</Trans>
                  : X:
                  {' '}
                  {pipePosition.x.toFixed(2)}
                  , Y:
                  {' '}
                  {pipePosition.y.toFixed(2)}
                </Typography>
              </Box>
            )}
          </FormSpacing>
        </Box>

        <Box sx={{ mt: 2 }}>
          <FormikActions
            formik={formik}
            mutation={mutation}
            submitText={i18n._('Create')}
            left={[
              [
                'viewPour',
                <Button
                  onClick={() => {
                    values.data.volume && onPreviewPour(values.data.volume, values.data.pourHeight, values.data.pasteBeachAngleDegrees);
                  }}
                  variant="contained"
                  color="secondary"
                  size="medium"
                  disabled={!values.data.volume || !values.recipeId}
                >
                  <Trans id="View Pour">View Pour</Trans>
                </Button>,
              ],
            ]}
            right={['reset', 'submit']}
          />
        </Box>
      </form>
    </Box>
  );
};

export default StopeFillPourFormScene;
